import { useEffect, useState } from "react";
import { Close, KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { Box, CircularProgress, IconButton, Modal } from "@mui/material";
import { FetchImageFromBackoffice } from "../../utils/requests";

const lightBoxStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  outline: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 80,
  minHeight: 80,
};

const closeButtonStyle = {
  position: "fixed",
  top: 16,
  right: 16,
  zIndex: 1301,
  color: "white",
};

const navButtonStyle = {
  position: "fixed",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 1301,
  color: "white",
};

export const LightBox = ({
  imageIds,
  vehicleVin,
  open,
  onClose,
  initialIndex = 0,
  onIndexChange,
  hideOverlay = false,
}: {
  imageIds: string[];
  vehicleVin: string | undefined;
  open: boolean;
  onClose: () => void;
  initialIndex?: number;
  onIndexChange?: (index: number) => void;
  hideOverlay?: boolean;
}) => {
  const [index, setIndex] = useState(initialIndex);
  const [imageUrl, setImageUrl] = useState<string>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setIndex(initialIndex);
    }
  }, [open, initialIndex]);

  const imageId = imageIds[index];

  useEffect(() => {
    if (!open || !vehicleVin || !imageId) return;

    let cancelled = false;
    let objectUrl: string | undefined;

    const fetchImage = async () => {
      setLoading(true);
      setImageUrl(undefined);
      try {
        const blob = await FetchImageFromBackoffice<Blob>(
          `portal/files/${vehicleVin}/files/${imageId}`
        );
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) {
          setImageUrl(objectUrl);
        }
      } catch (err) {
        console.warn("Lightbox image fetch failed:", err);
        if (!cancelled) {
          setImageUrl(undefined);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchImage();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [open, imageId, vehicleVin]);

  const goTo = (nextIndex: number) => {
    setIndex(nextIndex);
    onIndexChange?.(nextIndex);
  };

  const hasMultipleImages = imageIds.length > 1;
  const canPrev = index > 0;
  const canNext = index < imageIds.length - 1;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      slotProps={{
        backdrop: hideOverlay
          ? { sx: { backgroundColor: "transparent" } }
          : undefined,
      }}
    >
      <>
        <IconButton
          sx={{
            ...closeButtonStyle,
            ...(hideOverlay ? { color: "rgba(0, 0, 0, 0.7)" } : undefined),
          }}
          onClick={onClose}
        >
          <Close />
        </IconButton>
        {hasMultipleImages ? (
          <>
            <IconButton
              sx={{ ...navButtonStyle, left: 16 }}
              onClick={() => canPrev && goTo(index - 1)}
              disabled={!canPrev}
            >
              <KeyboardArrowLeft fontSize="large" />
            </IconButton>
            <IconButton
              sx={{ ...navButtonStyle, right: 16 }}
              onClick={() => canNext && goTo(index + 1)}
              disabled={!canNext}
            >
              <KeyboardArrowRight fontSize="large" />
            </IconButton>
          </>
        ) : null}
        <Box
          sx={{
            ...lightBoxStyle,
            ...(loading || !imageUrl
              ? { bgcolor: "transparent", boxShadow: "none" }
              : {}),
          }}
        >
          {loading || !imageUrl ? (
            <CircularProgress
              size={40}
              sx={{ color: hideOverlay ? "rgba(0, 0, 0, 0.54)" : "white" }}
            />
          ) : (
            <img
              src={imageUrl}
              alt=""
              style={{ display: "block", maxWidth: "60vw", maxHeight: "80vh" }}
            />
          )}
        </Box>
      </>
    </Modal>
  );
};
